import { CardElement, Elements, PaymentElement, useElements, useStripe, } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions, } from '@stripe/stripe-js';
import React, { useEffect, useMemo, useState } from 'react'
import { useCreatePaymentMethodDefaultMutation, useGetSavedPaymentMethodsQuery, useRemovePaymentMethodMutation, useSetupIntentQuery } from './api/billing.api';
import { Button } from '../../core/components';
import { Alert, Box, Card, Divider, IconButton, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import BlockContainer from '../../core/components/layout/BlockContainer';
import { CreditCard, CreditCardOff, Delete, DeleteOutline, Edit, EditOutlined } from '@mui/icons-material';
import ApiErrorLabel from '../../core/components/ui/ApiErrorLabel';
import ConfirmationDialog from '../../core/components/ui/ConfirmationDialog';
import { TypeChip } from './PaymentHistory';

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  , {
    betas: ['basil_checkout_beta_2']
  });

const PaymentMethodContext = ({ onRefetch }: { onRefetch: () => void; }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false)
  const { refetch } = useGetSavedPaymentMethodsQuery();
  const [isCompleted, setComleted] = useState(false)

  const handleSubmitDetails = async (event: any) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    try {
      if (!stripe || !elements) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return null;
      }
      setSubmitting(true)
      const { error, } = await stripe.confirmSetup({
        //`Elements` instance that was used to create the Payment Element
        elements,
        confirmParams: {},
        redirect: 'if_required', // 👈 prevent page reload,
      });
      if (error) {
        setErrorMessage(error.message);
      } else {
        await refetch();
        onRefetch()
        setSubmitting(false)
      }
    } catch (error) {

    } finally {
      setSubmitting(false)
    }
  }

  const handleChangePayment = (event) => {
    setComleted(event?.complete)
  }
  return (
    <form onSubmit={handleSubmitDetails}>
      {/* <Typography variant='h6' style={{ marginBottom: 10, fontSize: 16 }}>Add Payment Methods</Typography> */}
      <PaymentElement
        id='payment-element'
        onChange={handleChangePayment}
        options={{
          layout: {
            type: 'accordion',
            defaultCollapsed: false,
            radios: true,
            spacedAccordionItems: false,
          },

        }}
      />

      <Button style={{ marginTop: 24 }} type='submit' loading={isSubmitting} disabled={!isCompleted}>Save</Button>
    </form>
  )
}

const PaymentCard = ({ data, onDelete, onChange, isActive }: { data: any, onDelete: () => void, onChange: () => void, isActive: boolean }) => {
  const theme = useTheme();
  const isDefault = isActive && {
    borderColor: theme.palette.primary[400],
    bgcolor: theme.palette.primary.contrastText
  }
  return <Stack direction={'row'} border={1} padding={2} gap={4}
    borderRadius={2}
    borderColor={theme.palette.divider}
    sx={{
      cursor: 'pointer'
    }}
    onClick={() => onChange()}
    {...isDefault}
  >
    <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.primary.contrastText, borderRadius: 2, }}>
      <CreditCard sx={{ color: theme.palette.primary[400] }} />
    </Box>
    <Stack flex={1}>
      <Stack direction={'row'} spacing={3} alignItems={'center'}>
        <Typography variant='h6' style={{ fontSize: 16, color: theme.palette.primary[800] }}><span style={{ textTransform: 'capitalize' }}>{data?.card?.brand}</span> ending in {data?.card?.last4}</Typography>
        {isDefault && <Box sx={{
          fontSize: 12,
          borderRadius: 2,
          color: theme.palette.primary[400],
          fontWeight: '500'
        }}>Default</Box>}
      </Stack>
      <Typography variant='h6' style={{ fontSize: 13, color: theme.palette.text.disabled }}>Expires {data?.card?.exp_month}/{data?.card?.exp_year}</Typography>
    </Stack>
    <Stack direction={'row'} alignItems={'center'} gap={3}>
      <IconButton onClick={(event) => {
        event.stopPropagation();
        onDelete()
      }}>
        <DeleteOutline />
      </IconButton>
    </Stack>
  </Stack>
}

const SavedPaymentMethods = () => {
  const theme = useTheme()
  const { data, isLoading: savedMethodsLoading } = useGetSavedPaymentMethodsQuery();
  const savedMethods = data?.data?.data?.data;
  const [removeCard, { isError: isRemovePaymentMethodError, reset: removePaymentMethodReset }] = useRemovePaymentMethodMutation();
  const [updateDefault, { isError: isUpdatePaymentMethodError, reset: updatePaymentMethodReset }] = useCreatePaymentMethodDefaultMutation()
  const [isConfirmDelete, setConfirmDelete] = useState(false);
  const [isConfirmUpdate, setConfirmUpdate] = useState(false);
  const [confirmId, setConfirmId] = useState("");
  const [isActionLoading, setActionLoading] = useState(false)
  const handleDeleteCard = async (paymentMethodId: string) => {
    try {
      setActionLoading(true)
      const response = await removeCard({ paymentMethodId }).unwrap();
    } catch (error) {
      console.log("Error while deleting payment method:", error)
    } finally {
      setActionLoading(false);
      setConfirmDelete(false);
      setConfirmId("")
    }
  }

  const handleCreateDefaultPaymentMethod = async (paymentMethodId: string) => {
    try {
      setActionLoading(true)
      const response = await updateDefault({ paymentMethodId }).unwrap();
    } catch (error) {
      console.log("Error while updating payment method:", error);
    } finally {
      setActionLoading(false)
      setConfirmUpdate(false);
      setConfirmId("")
    }
  }

  return <Box>
    <Typography variant='h6' style={{ marginBottom: 10, fontSize: 16 }}>Saved Payment Methods</Typography>
    <Stack spacing={2}>
      {savedMethods && savedMethods.map((item, index) => {
        return <PaymentCard data={item} key={index}
          onChange={() => {
            setConfirmUpdate(true);
            setConfirmId(item.id)
          }}
          onDelete={() => {
            setConfirmDelete(true);
            setConfirmId(item.id)
          }}
          isActive={item.isDefault}
        />
      })}
    </Stack>
    <ApiErrorLabel
      show={isRemovePaymentMethodError || isUpdatePaymentMethodError}
      message={"Something went wrong! Please try again!"}
      onDismiss={() => {
        removePaymentMethodReset();
        updatePaymentMethodReset()
      }}
    />
    <ConfirmationDialog
      open={isConfirmDelete}
      title='Delete Payment Method'
      message='Are you sure you want to delete payment method!'
      onConfirm={() => handleDeleteCard(confirmId)}
      onCancel={() => {
        setConfirmDelete(false);
        setConfirmId("");
      }}
      confirmLabel='Yes Delete it'
      cancelLabel='Cancel'
      isLoading={isActionLoading}
      confirmSx={{
        style: {
         backgroundColor:"red"
       }
      }}
    />
    <ConfirmationDialog
      open={isConfirmUpdate}
      title='Change Default Payment Method'
      message='Are you sure you want to change the default payment method!'
      onConfirm={() => handleCreateDefaultPaymentMethod(confirmId)}
      onCancel={() => {
        setConfirmUpdate(false);
        setConfirmId("");
      }}
      confirmLabel='Yes Change it'
      cancelLabel='Cancel'
      isLoading={isActionLoading}
      confirmSx={{
        style: {
          backgroundColor: theme.palette.primary[400]
        }
      }}
    />
  </Box>
}

function PaymentMethods() {
  const { data, isLoading, isError, refetch } = useSetupIntentQuery();
  const theme = useTheme();
  const clientSecret = data?.data?.data?.setup_secret || "";
  const customerSessionClientSecret = data?.data?.data?.customer_session_client_secret;
  const hasValidSecret = !!clientSecret;
  const [forceReload, setForceReload] = useState(true)
  const elementsOptions: StripeElementsOptions = {
    clientSecret: clientSecret,
    // customerSessionClientSecret,
  };
  const handleRefetchSecret = async () => {
    await refetch();
    setForceReload(false)
    setTimeout(() => {
      setForceReload(true)
    }, 500);
  }


  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 4
    }}>
      <BlockContainer sx={{ paddingLeft: '10px', paddingRight: '10px' }}>
        <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} gap={3} mb={3}>
          <CreditCard sx={{ color: theme.palette.primary[400] }} />
          <Typography variant='h6' sx={{ color: theme.palette.grey[800], fontWeight: 600 }}>Your Payment Methods</Typography>
        </Stack>
        {hasValidSecret && <SavedPaymentMethods />}
        {hasValidSecret && <Divider sx={{marginTop:3, marginBottom:3}}/>}
        {isLoading ? <ElementsSkeleton /> :
          hasValidSecret && forceReload ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <PaymentMethodContext onRefetch={handleRefetchSecret} />
            </Elements>
          ) : !hasValidSecret ? (
            <NoPaymentMethods />
          ) : <ElementsSkeleton />
        }
      </BlockContainer>
    </Box>
  )
}

export default PaymentMethods

const NoPaymentMethods = () => {
  const theme = useTheme();
  return (
    <Stack
      spacing={2}
      alignItems="center"
      justifyContent="center"
      border={1}
      borderColor={theme.palette.divider}
      borderRadius={2}
      padding={4}
      sx={{ minHeight: 180 }}
    >
      <CreditCardOff sx={{ fontSize: 48, color: theme.palette.grey[400] }} />
      <Typography variant="h6" sx={{ fontSize: 16, color: theme.palette.grey[600], fontWeight: 500 }}>
        No Payment Methods
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.grey[500], textAlign: 'center' }}>
        There are no payment methods on file. Payment methods will appear here once a subscription is activated.
      </Typography>
    </Stack>
  );
};

const ElementsSkeleton = () => {
  const theme = useTheme()
  return <Stack spacing={3} border={1} borderColor={theme.palette.divider} padding={3} borderRadius={2}>
    <Skeleton variant="rectangular" width={'100%'} height={40} sx={{ borderRadius: 2 }} />
    <Skeleton variant="rectangular" width={'100%'} height={40} sx={{ borderRadius: 2 }} />
    <Skeleton variant="rectangular" width={'100%'} height={40} sx={{ borderRadius: 2 }} />
    <Skeleton variant="rectangular" width={'100%'} height={40} sx={{ borderRadius: 2 }} />
    <Skeleton variant="rectangular" width={'100%'} height={40} sx={{ borderRadius: 2 }} />
    <Skeleton variant="rectangular" width={'100%'} height={40} sx={{ borderRadius: 2 }} />
    <Skeleton variant="rectangular" width={'100%'} height={40} sx={{ borderRadius: 2 }} />
  </Stack>
}